// Remembers, per till, what has already been sent to each slip printer for an
// order, so printing a draft and then paying it (or paying after adding an
// item) never prints the same item twice.
//
//   "01pos_slip_ledger": {
//     [orderNo]: { ts, routes: { [routeId]: { n, items: { [itemKey]: qtySent } } } }
//   }
//
// n counts the slips the route has had for the order (used in the job key and
// to decide if the next one is an ADD-ON). The shape adds `n` and `routes` to
// the agreed { [routeId]: { [itemKey]: qty } } because a count has to live
// somewhere that can't collide with an item name.

const LEDGER_KEY = "01pos_slip_ledger";
const MAX_AGE_MS = 48 * 60 * 60 * 1000;

// Items sent to a route are keyed by product id, else by name.
export const itemKeyOf = (item) =>
  item?.productId != null && item.productId !== ""
    ? `id:${item.productId}`
    : `name:${item?.name ?? ""}`;

const readAll = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(LEDGER_KEY));
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const prune = (ledger, now) => {
  for (const orderNo of Object.keys(ledger)) {
    const ts = Number(ledger[orderNo]?.ts);
    if (!Number.isFinite(ts) || now - ts > MAX_AGE_MS) delete ledger[orderNo];
  }
  return ledger;
};

const writeAll = (ledger) => {
  try {
    localStorage.setItem(LEDGER_KEY, JSON.stringify(ledger));
  } catch {
    // Storage unavailable: duplicates become possible, printing still works.
  }
};

export const getRouteEntry = (orderNo, routeId, now = Date.now()) => {
  const entry = prune(readAll(), now)[String(orderNo)]?.routes?.[routeId];
  return { n: Number(entry?.n) || 0, items: entry?.items || {} };
};

// What is new for a route compared with the ledger: whole new items, plus the
// increase for items whose quantity went up. Decreases and removals are ignored.
export const computeDelta = (entry, items) => {
  const wanted = new Map(); // itemKey -> { item, quantity }
  for (const item of items) {
    const key = itemKeyOf(item);
    const quantity = Number(item.quantity) || 0;
    const existing = wanted.get(key);
    if (existing) existing.quantity += quantity;
    else wanted.set(key, { item, quantity });
  }
  const delta = [];
  for (const [key, { item, quantity }] of wanted) {
    const increase = quantity - (Number(entry.items[key]) || 0);
    if (increase > 0) delta.push({ ...item, quantity: increase });
  }
  return delta;
};

// Call only after the bridge accepted the job.
export const recordSent = (orderNo, routeId, sentItems, now = Date.now()) => {
  const ledger = prune(readAll(), now);
  const order = (ledger[String(orderNo)] ||= { ts: now, routes: {} });
  order.ts = now;
  const entry = (order.routes[routeId] ||= { n: 0, items: {} });
  entry.n += 1;
  for (const item of sentItems) {
    const key = itemKeyOf(item);
    entry.items[key] = (Number(entry.items[key]) || 0) + (Number(item.quantity) || 0);
  }
  writeAll(ledger);
};
